import {
  ecorpResources,
  type DocumentType,
  type EcorpResourceKey,
  type ReportCategory,
} from '@command-center/admin-ecorp';
import { authedFetch } from './auth';

const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
export interface PortalRecord {
  id: string;
  createdTime?: string;
  fields: Record<string, unknown>;
}
export interface RecordPage {
  items: PortalRecord[];
  offset?: string;
  total?: number;
}

export interface FieldSuggestion {
  id: string;
  extraction_run_id: string;
  target_entity: string;
  target_airtable_record_id: string | null;
  field_key: string;
  extracted_value: unknown;
  existing_value: unknown;
  confidence: number | null;
  inferred: boolean;
  conflict: boolean;
  source_page: number | null;
  source_sheet: string | null;
  source_citation: string | null;
  notes: string | null;
  review_status: 'pending' | 'accepted' | 'edited' | 'rejected' | 'superseded';
}

export interface ExtractionComponent {
  category: string;
  name: string;
  details: string | null;
  quantity: number | null;
  sourcePage: number | null;
  sourceSheet: string | null;
}
export interface ExtractionMeta {
  confidence: number;
  extractionSummary: string;
  missingInformation: string[];
  conflicts: string[];
  warnings: string[];
  keyFacts: string[];
  technicalReportName?: string | null;
  reportCategory?: ReportCategory | null;
  outcomeDetermination?: string | null;
  importantMetrics?: Array<Record<string, unknown>>;
  standardsThresholds?: Array<Record<string, unknown>>;
  issuesExceedances?: Array<Record<string, unknown>>;
  findingsConclusions?: Array<Record<string, unknown>>;
  mitigationConditions?: Array<Record<string, unknown>>;
}
export interface ExtractionRunStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string | null;
  runId?: string;
  suggestions?: FieldSuggestion[];
  components?: ExtractionComponent[];
  normalized?:
    | (ExtractionMeta & {
        planTitle?: string | null;
        planDate?: string | null;
        revision?: string | null;
      })
    | null;
}
export interface SuggestionDecision {
  id: string;
  action: 'accept' | 'edit' | 'reject' | 'pending';
  value?: unknown;
}
export interface SearchResult {
  resourceType: 'project' | 'client' | 'contact' | 'document';
  recordId: string;
  title: string;
  subtitle: string;
  metadata: Record<string, string>;
  url: string;
}
export interface ActivityItem {
  id: string;
  created_at: string;
  actor_name: string | null;
  actor_email: string | null;
  entity_type: string;
  entity_id: string | null;
  entity_label: string | null;
  action: string;
  summary: string | null;
  notes: string | null;
  category: string | null;
  metadata: Record<string, unknown>;
}
export interface ProvenanceItem {
  id: string;
  field_key: string;
  previous_value: unknown;
  applied_value: unknown;
  source_airtable_document_record_id: string | null;
  source_label: string | null;
  source_page: number | null;
  source_sheet: string | null;
  source_citation: string | null;
  applied_by_name: string | null;
  applied_at: string;
}
export interface AliceProposal {
  type: string;
  projectRecordId: string;
  projectName: string;
  fieldKey: string;
  existingValue: unknown;
  proposedValue: unknown;
  reason: string;
  source: string;
}
export interface AliceAttachmentPayload {
  name: string;
  mimeType: string;
  dataUrl: string;
}
export interface AliceMentionPayload {
  type: 'project' | 'client' | 'contact';
  id: string;
  name: string;
}
export interface AliceFramework {
  configuration: {
    identity: string;
    status: string;
    organization: string;
    application: string;
    provider: string;
    model: string | null;
    communicationMode: string;
    contextScope: string[];
    roles: string[];
  };
  tools: Array<{ id: string; name: string; description: string; enabled: boolean; scope: string }>;
  connections: Array<{ id: string; name: string; purpose: string; status: string }>;
}
export interface MissingItem {
  group: string;
  field: string;
  label: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authedFetch(`${base}${path}`, init);
  const body = (await response.json().catch(() => null)) as
    | (T & { error?: { message?: string } })
    | null;
  if (!response.ok) throw new Error(body?.error?.message ?? `Request failed (${response.status}).`);
  return body as T;
}

const routeFor = (resource: EcorpResourceKey) => ecorpResources[resource].routePath;

export function listRecords(resource: EcorpResourceKey, params: URLSearchParams) {
  return request<RecordPage>(`/api/admin/${routeFor(resource)}?${params}`);
}
export function getRecord(resource: EcorpResourceKey, id: string) {
  return request<{ item: PortalRecord }>(
    `/api/admin/${routeFor(resource)}/${encodeURIComponent(id)}`,
  );
}
export function createRecord(resource: EcorpResourceKey, fields: Record<string, unknown>) {
  return request<{ item: PortalRecord }>(`/api/admin/${routeFor(resource)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
}
export function updateRecord(
  resource: EcorpResourceKey,
  id: string,
  fields: Record<string, unknown>,
) {
  return request<{ item: PortalRecord }>(
    `/api/admin/${routeFor(resource)}/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    },
  );
}
export async function deleteRecord(
  resource: EcorpResourceKey,
  id: string,
  options: { confirmMainPoc?: boolean } = {},
): Promise<void> {
  const params = options.confirmMainPoc ? '?confirmMainPoc=true' : '';
  const response = await authedFetch(
    `${base}/api/admin/${routeFor(resource)}/${encodeURIComponent(id)}${params}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(body?.error?.message ?? `Delete failed (${response.status}).`);
  }
}

// ---- Documents pipeline ----

export interface UploadOptions {
  projectId?: string;
  clientId?: string;
  documentName?: string;
  documentType?: DocumentType;
  reportCategory?: ReportCategory;
  documentDate?: string;
  revision?: string;
}

export async function uploadDocument(
  file: File,
  options: UploadOptions,
): Promise<{
  item?: PortalRecord;
  intake?: { assetId: string | null; documentUuid: string };
  asset?: { id: string | null; documentUuid: string };
}> {
  const params = new URLSearchParams({ filename: file.name });
  for (const [key, value] of Object.entries(options))
    if (value)
      params.set(key === 'projectId' ? 'projectId' : key === 'clientId' ? 'clientId' : key, value);
  const response = await authedFetch(`${base}/api/admin/documents/upload?${params}`, {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
  if (!response.ok) throw new Error(body?.error?.message ?? `Upload failed (${response.status}).`);
  return body as never;
}

export async function fetchDocumentBlob(id: string): Promise<Blob> {
  const response = await authedFetch(
    `${base}/api/admin/documents/${encodeURIComponent(id)}/download`,
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(body?.error?.message ?? `Document download failed (${response.status}).`);
  }
  return response.blob();
}
export async function downloadDocument(id: string, filename: string): Promise<void> {
  const blob = await fetchDocumentBlob(id);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'document';
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

export function extractDocument(id: string, processor?: string) {
  return request<{ runId: string; status: 'pending' }>(
    `/api/admin/documents/${encodeURIComponent(id)}/extract`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ processor }),
    },
  );
}
export function getExtractionRunStatus(runId: string) {
  return request<ExtractionRunStatus>(
    `/api/admin/extraction-runs/${encodeURIComponent(runId)}/status`,
  );
}
export function getReviewSummary() {
  return request<{ readyForReview: number; failed: number }>(`/api/admin/documents/review-summary`);
}
export function getSuggestions(id: string) {
  return request<{
    run: { id: string; status: string; started_at?: string } | null;
    suggestions: FieldSuggestion[];
    components: ExtractionComponent[];
    normalized: ExtractionMeta | null;
  }>(`/api/admin/documents/${encodeURIComponent(id)}/suggestions`);
}
export function applySuggestions(
  id: string,
  payload: { decisions?: SuggestionDecision[]; bulk?: boolean },
) {
  return request<{
    appliedCount: number;
    rejectedCount: number;
    skipped: string[];
    project: PortalRecord | null;
  }>(`/api/admin/documents/${encodeURIComponent(id)}/apply-suggestions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// ---- Site-plan-first flow ----

export function extractIntake(assetId: string) {
  return request<{ runId: string; status: 'pending' }>(
    `/api/admin/intake/${encodeURIComponent(assetId)}/extract`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) },
  );
}
export function createProjectFromSitePlan(payload: {
  assetId: string;
  runId?: string | null;
  projectFields: Record<string, unknown>;
  clientRecordId?: string;
  newClient?: Record<string, unknown>;
  documentMeta?: Record<string, string | undefined>;
  decisions?: SuggestionDecision[];
}) {
  return request<{
    item: PortalRecord;
    documentRecordId: string;
    analysisRecordId?: string;
    applied: number;
  }>(`/api/admin/projects/from-site-plan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
export function checkDuplicates(params: {
  name?: string;
  address?: string;
  parcelNumbers?: string;
  clientName?: string;
}) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
  return request<{ projects: PortalRecord[]; clients: PortalRecord[] }>(
    `/api/admin/projects/duplicate-check?${query}`,
  );
}

// ---- Search / activity / micro-tools ----

export function globalSearch(query: string) {
  return request<{ results: SearchResult[] }>(`/api/admin/search?q=${encodeURIComponent(query)}`);
}
export function listActivity(entityType?: string, entityId?: string) {
  const params = new URLSearchParams();
  if (entityType) params.set('entityType', entityType);
  if (entityId) params.set('entityId', entityId);
  return request<{ items: ActivityItem[] }>(`/api/admin/activity?${params}`);
}
export function getProvenance(projectId: string, field?: string) {
  return request<{ items: ProvenanceItem[] }>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/provenance${field ? `?field=${encodeURIComponent(field)}` : ''}`,
  );
}
export function getProjectBrief(projectId: string) {
  return request<{ brief: string }>(`/api/admin/projects/${encodeURIComponent(projectId)}/brief`);
}
export function getMissingInfo(projectId: string) {
  return request<{ missing: MissingItem[] }>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/missing-info`,
  );
}

// ---- Alice ----

export function askAlice(
  messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  context: Record<string, unknown>,
  options: {
    model?: string;
    attachments?: AliceAttachmentPayload[];
    mentions?: AliceMentionPayload[];
  } = {},
) {
  return request<{
    answer: string;
    toolCalls: Array<{ tool: string; args: Record<string, unknown> }>;
    proposals: AliceProposal[];
  }>(`/api/admin/alice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, context, ...options }),
  });
}

export function getAliceFramework() {
  return request<AliceFramework>('/api/admin/alice/framework');
}
